import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Flex } from 'antd-v5';
import cn from 'classnames';
import { capitalize } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';
import { MdOutlineWarning } from 'react-icons/md';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import { useStorePackRosterFilter } from '@modules/advancement/packRoster/duck/hooks';
import { showSubUnitsSel } from '@modules/advancement/packRoster/duck/selectorsTyped';
import {
  setShowSubUnits,
  toggleGroupByOrderStatus,
} from '@modules/advancement/packRoster/duck/slice';
import { deleteSubUnitRequest } from '@modules/advancement/subUnits/duck/actions';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { useRosterPermissions } from '@modules/permissionManager/duck/hooks';
import { useGetUnitOrdersQuery } from '@modules/roster/api';
import { isInactiveUnitSel } from '@modules/unit/duck/selectorsTyped';
import {
  hasNewPermissionSel,
  sorterPropTypes,
  unitTypeIdForUnitType,
} from '@shared';
import {
  Button,
  Modal,
  PaginationTotal,
  S,
  SingleConfirmationModal,
  Table,
} from '@shared/components';
import { selectedOrganizationSel } from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import { toggleArrayItem } from '@utils';

import { subUnitTypeSel } from '../../../../subUnits/duck/selectors';
import { SELECTABLE_PEOPLE_LIMIT, peopleFilters } from '../../../constants';
import {
  currentUserSubunitsSel,
  loadingRenewalSel,
  packRosterFilter,
  saveOptoutProfile,
  setShowMemberTypes,
  setSubUnitUserData,
  showMemberTypesSel,
  subUnitUserDataSel,
} from '../../../duck';
import { qaRowClassName } from '../../../helpers';
import CustomSelectionTitle from './CustomSelectionTitle';
import CustomMainRow from './MainRow';
import OrderStatusRowGroup from './OrderStatusRowGroup/OrderStatusRowGroup';
import styles from './PackRosterTable.less';
import PackRosterTableHeader from './PackRosterTableHeader';
import CustomTypeMainRow from './TypeMainRow';
import columns from './packRosterTableColumns';

const PackRosterTable = props => {
  const {
    loading,
    items,
    itemsPerSubUnit,
    itemsPerMemberType,
    itemsPerOrderStatus,
    groupedByOrderStatus = false,
    subUnitViewActive,
    selectedRowKeys,
    onSelectedRowKeysChange,
    sorter,
    search,
  } = props;

  const dispatch = useDispatch();
  const { unitTypeId, organizationGuid } = useSelector(selectedOrganizationSel);
  const canEditMySubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_SUB_UNIT),
  );

  const canEditAllYouthProfiles = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_PROFILE),
  );

  const canSeeLeaderProfiles = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.VIEW_ADULT_PROFILE_PAGE),
  );

  const canSeeYouthProfiles = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.VIEW_YOUTH_PROFILE_PAGE),
  );

  const canSeeMySubunitProfiles = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_SUB_PROFILE),
  );

  const hasSubUnitOnlyPerms = useMemo(
    () => canEditMySubUnits,
    [canEditMySubUnits],
  );
  useGetUnitOrdersQuery(
    { organizationGuid },
    {
      skip:
        !featureFlags.getFlag('SBL_5483_GROUPBY_ORDER_STATUS') ||
        !organizationGuid,
    },
  );
  const subUnitTypeName = useSelector(subUnitTypeSel);
  const subUnitUserData = useSelector(subUnitUserDataSel);
  const isInactiveUnit = useSelector(isInactiveUnitSel);
  const mySubUnits = useSelector(currentUserSubunitsSel);
  const showSubUnits = useSelector(showSubUnitsSel);
  const showMemberTypes = useSelector(showMemberTypesSel);
  const canEditAllSubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_UNIT),
  );
  const areRenewalsLoading = useSelector(loadingRenewalSel);

  const storePackRosterFilter = useStorePackRosterFilter();

  const closeDeleteModalRef = useRef();
  const [defaultSubUnitList, setDefaultSubUnitList] = useState([]);
  const [optOutData, setOptOutData] = useState();
  const [isConfirmOptOutVisible, setIsConfirmOptOutVisible] = useState(false);

  const orgIsPack = useMemo(
    () => +unitTypeId === unitTypeIdForUnitType.Pack,
    [unitTypeId],
  );

  const {
    unitRequiredRosterPositions,
    rosterAllPositions,
    subUnitRequiredRosterPositions,
  } = useRosterPermissions();

  useEffect(() => {
    if (subUnitUserData.subUnitId && subUnitUserData.subUnitId > 0) {
      closeDeleteModalRef.current.openConfirmationModal();
    }
  }, [dispatch, subUnitUserData]);

  useEffect(() => {
    const unnasignedMembers = props.items || [];
    const values = unnasignedMembers.map(member => ({
      userId: member.userId,
      subUnitId: member.subUnitId,
    }));
    setDefaultSubUnitList(values);
  }, [props.items]);

  const handleTableChange = (pagination, filters, sorter) => {
    props.onSorterChange(sorter);
  };

  const shouldDisableRecord = useCallback(
    record => {
      if (isInactiveUnit) return true;
      if (!hasSubUnitOnlyPerms && !canEditAllSubUnits) {
        return record.isAdult ? !canSeeLeaderProfiles : !canSeeYouthProfiles;
      }
      if (!hasSubUnitOnlyPerms || canEditAllSubUnits) return !record.userId;

      if (record.subUnitId === -1) return true;

      if (record.userId) {
        const hasMatchingSubUnits = mySubUnits.some(sub => {
          const mySubId = sub.denId || sub.patrolId;
          return record.isLeader
            ? record.subUnitListIds.includes(mySubId)
            : mySubId === record.subUnitId;
        });
        return !hasMatchingSubUnits;
      }

      return !record.userId;
    },
    [
      hasSubUnitOnlyPerms,
      mySubUnits,
      canEditAllSubUnits,
      isInactiveUnit,
      canSeeLeaderProfiles,
      canSeeYouthProfiles,
    ],
  );

  const handleRowClick = record => () => {
    if (shouldDisableRecord(record)) return;

    const { userId, subUnitId } = record;
    const { selectedRowKeys } = props;
    if (userId) {
      const newKeys = toggleArrayItem(
        selectedRowKeys,
        `${userId}${subUnitId ? `*${subUnitId}` : ''}`,
      );
      props.onSelectedRowKeysChange(newKeys);
    }
  };

  const getAllRowsIds = memoize(items =>
    items.map(
      ({ userId, subUnitId }) => `${userId}${subUnitId ? `*${subUnitId}` : ''}`,
    ),
  );

  const getRowClassName = row => {
    const { selectedRowKeys } = props;
    const isRowSelected = selectedRowKeys.includes(row.userId);
    const isDisabled = !row.userId;

    return cn(
      {
        selected: isRowSelected,
        [styles.unselectableRow]:
          row.isMainRow ||
          (!isRowSelected && selectedRowKeys.length == SELECTABLE_PEOPLE_LIMIT),
        [styles.disabledRow]: isDisabled,
      },
      qaRowClassName(row),
    );
  };

  const getPersonRowKey = rowData => {
    const { userId, memberId, subUnitId } = rowData;
    if (groupedByOrderStatus && rowData.key) return rowData.groupId;
    return `${userId || memberId}${subUnitId ? `*${subUnitId}` : ''}`;
  };

  const getPaginationConfig = () => {
    const { selectedRowKeys } = props;

    return {
      className: 'qa_pagination',
      showTotal: total => (
        <PaginationTotal total={total} selected={selectedRowKeys.length} />
      ),
    };
  };

  const renderHeader = () => {
    const { selectedItems, selectedRowKeys, isOffline } = props;

    return (
      <PackRosterTableHeader
        selectedItems={selectedItems}
        selectedRowKeys={selectedRowKeys}
        isOffline={isOffline}
      />
    );
  };

  const handleSelectAllItems = () => {
    const { items, onSelectedRowKeysChange } = props;
    const validItems = items.filter(
      record => !!record.userId && !shouldDisableRecord(record),
    );
    const selectedItemsKeys = getAllRowsIds(validItems);
    onSelectedRowKeysChange(selectedItemsKeys);
  };

  const handleDeselectAllItems = () => props.onSelectedRowKeysChange([]);

  const areAllSubUnitItemsSelected = (subUnitItems, selectedRowKeys) =>
    subUnitItems.every(({ userId, subUnitId }) =>
      selectedRowKeys.includes(`${userId}${subUnitId ? `*${subUnitId}` : ''}`),
    );

  const areSomeSubUnitItemsSelected = (subUnitItems, selectedRowKeys) =>
    subUnitItems.some(({ userId, subUnitId }) =>
      selectedRowKeys.includes(`${userId}${subUnitId ? `*${subUnitId}` : ''}`),
    );

  const handleSelectSubUnitItems = subUnitId => {
    const hasMatchingSubUnits = mySubUnits.some(sub => {
      const mySubId = sub.denId || sub.patrolId;
      return mySubId === subUnitId;
    });

    if (hasSubUnitOnlyPerms && !canEditAllSubUnits && !hasMatchingSubUnits)
      return;

    const { itemsPerSubUnit, selectedRowKeys, onSelectedRowKeysChange } = props;

    const subUnitItems = itemsPerSubUnit.find(
      subUnit => subUnit.subUnitId == subUnitId,
    ).subRows;
    const subUnitItemsKeys = subUnitItems.map(
      ({ userId, subUnitId }) => `${userId}${subUnitId ? `*${subUnitId}` : ''}`,
    );
    const areAllSelected = areAllSubUnitItemsSelected(
      subUnitItems,
      selectedRowKeys,
    );
    const selectedRows = areAllSelected
      ? selectedRowKeys.filter(key => !subUnitItemsKeys.includes(key))
      : subUnitItemsKeys;
    onSelectedRowKeysChange(selectedRows);
  };

  const transform = items => {
    const { selectedRowKeys } = props;
    return items.map(subUnitSection => ({
      ...subUnitSection,
      areAnyItemsSelected: areSomeSubUnitItemsSelected(
        subUnitSection.subRows,
        selectedRowKeys,
      ),
      itemsCount: subUnitSection.subRows.length,
      onSelect: () => handleSelectSubUnitItems(subUnitSection.subUnitId),
    }));
  };

  const getMainRowsConfig = () => {
    if (groupedByOrderStatus) {
      return {
        colSpan: 11,
        CustomMainRow: OrderStatusRowGroup,
      };
    }
    if (props.subUnitViewActive) {
      return {
        CustomMainRow,
        colSpan: 11,
      };
    }
    if (showMemberTypes) {
      return {
        CustomMainRow: CustomTypeMainRow,
        colSpan: 11,
      };
    }
  };

  const itemsAmount = memoize(items =>
    items.reduce((acc, item) => {
      const count = item.userId ? 1 : 0;
      return acc + count;
    }, 0),
  );
  const handleconfirmDelete = () => {
    dispatch(deleteSubUnitRequest(subUnitUserData.subUnitId));
    dispatch(setSubUnitUserData({ subUnitId: 0, subUnitName: '' }));
  };

  const handleCancelDelete = () => {
    dispatch(setSubUnitUserData({ subUnitId: 0, subUnitName: '' }));
  };

  const handleClickSubUnitHeader = () => {
    dispatch(setShowMemberTypes(false));
    dispatch(setShowSubUnits({ show: !showSubUnits }));
    storePackRosterFilter({
      showSubUnits: !showSubUnits,
    });
  };

  const handleClickTypeHeader = () => {
    dispatch(
      packRosterFilter(
        Object.values(peopleFilters).reduce(
          (acc, f) => ({ ...acc, [f]: true }),
          {},
        ),
      ),
    );
    dispatch(setShowSubUnits({ show: false, orgGuid: organizationGuid }));
    dispatch(setShowMemberTypes(!showMemberTypes));
  };

  const onGroupByOrderStatus = () => {
    dispatch(toggleGroupByOrderStatus(true));
  };

  const onSaveOptOut = data => {
    setOptOutData(data);
    setIsConfirmOptOutVisible(true);
  };

  const onConfirmOptOut = () => {
    dispatch(saveOptoutProfile(optOutData));
    setIsConfirmOptOutVisible(false);
  };

  const onCancelOptOut = () => {
    setOptOutData(undefined);
    setIsConfirmOptOutVisible(false);
  };

  const itemsPresentation = groupedByOrderStatus
    ? itemsPerOrderStatus
    : showMemberTypes
    ? itemsPerMemberType
    : subUnitViewActive
    ? transform(itemsPerSubUnit)
    : items;

  const shouldShowEmptyWarning = () => {
    let hasItems = itemsPresentation.length;
    if (subUnitViewActive || showMemberTypes) {
      hasItems = itemsPresentation.some(item => item.itemsCount > 0);
    }

    return !hasItems && !loading && !search;
  };

  const rowSelection = {
    selectedRowKeys,
    getCheckboxProps: record => ({
      disabled: shouldDisableRecord(record),
    }),
    columnTitle: (
      <CustomSelectionTitle
        selectedItemsAmount={selectedRowKeys.length}
        itemsAmount={itemsAmount(items)}
        onSelectAll={handleSelectAllItems}
        onDeselectAll={handleDeselectAllItems}
      />
    ),
    onChange: onSelectedRowKeysChange,
  };

  return (
    <Fragment>
      <SingleConfirmationModal
        ref={closeDeleteModalRef}
        message={
          <FormattedHTMLMessage
            id="packRoster.MainRow.confirmDeleteSubUnit"
            values={{
              subUnitName: capitalize(subUnitUserData.subUnitName),
              subUnitTypeName: capitalize(subUnitTypeName),
            }}
          />
        }
        onConfirm={handleconfirmDelete}
        onCancel={handleCancelDelete}
        roundedModal
      />
      <Table
        size="middle"
        fixedLayout
        clickableRows
        className={cn('Joyride__PackRosterTable', styles.table)}
        loading={loading}
        rowSelection={rowSelection}
        rowKey={getPersonRowKey}
        rowClassName={getRowClassName}
        title={renderHeader}
        columns={columns(
          {
            ...props,
            showSubUnits,
            isInactiveUnit,
            canEditAllSubUnits,
            defaultSubUnitList,
            areRenewalsLoading,
            orgIsPack,
            unitTypeId,
            hasSubUnitOnlyPerms,
            canEditAllYouthProfiles,
            canSeeYouthProfiles,
            canSeeLeaderProfiles,
            canSeeMySubunitProfiles,
            mySubUnits,
            organizationGuid,
            onSaveOptOut,
            onGroupByOrderStatus,
            unitRequiredRosterPositions,
            rosterAllPositions,
            subUnitRequiredRosterPositions,
          },
          styles,
          handleClickSubUnitHeader,
          handleClickTypeHeader,
        )}
        dataSource={itemsPresentation}
        onChange={handleTableChange}
        sorter={sorter}
        onRow={record => ({
          onClick: handleRowClick(record),
        })}
        pagination={getPaginationConfig()}
        mainRows={getMainRowsConfig()}
        scroll={{ x: true }}
        floatingHorizontalScroll
      />
      {shouldShowEmptyWarning() && (
        <Flex justify="center">
          <Text>
            <MdOutlineWarning color="#ffb708" />
            <FormattedMessage id="advancement.PackRoster.filterWarning" />
          </Text>
        </Flex>
      )}
      <Modal
        visible={isConfirmOptOutVisible}
        onCancel={onCancelOptOut}
        closable={false}
      >
        <S size="3">
          <FormattedMessage id="advancement.PackRoster.confirmOptOut" />
        </S>
        <div className={styles.modalButtonsContainer}>
          <Button onClick={onCancelOptOut} shape="round" size="small">
            {<FormattedMessage id="shared.no" />}
          </Button>
          <Button
            type="primary"
            onClick={onConfirmOptOut}
            shape="round"
            size="small"
          >
            {<FormattedMessage id="shared.yes" />}
          </Button>
        </div>
      </Modal>
    </Fragment>
  );
};

PackRosterTable.propTypes = {
  items: PropTypes.array.isRequired,
  itemsPerSubUnit: PropTypes.array.isRequired,
  itemsPerMemberType: PropTypes.array.isRequired,
  groupedByOrderStatus: PropTypes.bool,
  itemsPerOrderStatus: PropTypes.array,
  selectedItems: PropTypes.array.isRequired,
  subUnitViewActive: PropTypes.bool.isRequired,
  filter: PropTypes.object.isRequired,
  sorter: sorterPropTypes,
  search: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  selectedRowKeys: PropTypes.array.isRequired,
  isOffline: PropTypes.bool.isRequired,
  onSorterChange: PropTypes.func.isRequired,
  onSelectedRowKeysChange: PropTypes.func.isRequired,
};

export default PackRosterTable;
